import { Card, CardAction, CardContent, CardHeader, CardTitle, ScrollArea } from '@ibsys/kit';
import { FC, ReactNode } from 'react';
import { DASHBOARD_BLOCK_MAX_HEIGHT_CLASS, cn } from '@/shared/lib';
import { TruncatedText } from '@/shared/ui';
import { useChartCardHeaderAction } from './ChartCardHeaderContext';
import { chartTableStyle } from './chartLayout';

type Props = {
  title: string;
  /**
   * Прокручивать содержимое внутри карточки при переполнении. Отключается у блоков со своей
   * прокруткой (таблицы): вложенная прокрутка внутри прокрутки неуправляема мышью.
   */
  scrollable?: boolean;
  /**
   * Подвал содержимого без общей прокрутки — легенда таблицы: стоит под прокручиваемым местом
   * таблицы и сам не прокручивается. Не задан — таблица занимает всё содержимое.
   */
  footer?: ReactNode;
  /** Высота диаграммы задана пользователем — место таблицы над подвалом не уже наименьшей таблицы */
  hasFixedHeight?: boolean;
  children: ReactNode;
};

/**
 * Карточка диаграммы пользовательского дашборда: заголовок и содержимое, ограниченное отведённой
 * диаграмме высотой и общим потолком высоты блоков дашборда.
 *
 * Не поместившееся содержимое прокручивается внутри самой диаграммы — по каждой стороне отдельно
 * и только там, где места не хватило: узкой доле ширины широкий тип отдаётся прокруткой, а не
 * сжатием, зато вставшая целиком диаграмма полос не показывает вовсе.
 *
 * Содержимое со своей прокруткой стоит колонкой: подвал (легенда таблицы) не сжимается и стоит
 * под ним, а таблица сжимается до остатка высоты. Прокручивается таблица и по месту, которое за
 * собой держит: у наименьших заданных высот она не сжимается до одной шапки, а выходит за
 * отведённое ей место, и до её строк добираются прокруткой этого места. Подвал в прокрутку не
 * входит, поэтому строки не уходят ни под него, ни в нижний отступ диаграммы.
 *
 * С подвалом при заданной высоте место таблицы само не уже наименьшей таблицы: над подвалом ему
 * иначе осталось бы меньше шапки, и ни одной строки не прочесть. Диаграмма тогда встаёт не ниже,
 * чем нужно заголовку, наименьшей таблице и подвалу, даже если задана высота меньше.
 *
 * Рядом с названием встаёт управление диаграммой — переключатель её разреза: он приходит
 * контекстом, поэтому представлению типа о нём знать незачем.
 */
export const ChartCardShell: FC<Props> = ({ title, scrollable = true, footer, hasFixedHeight, children }) => {
  const headerAction = useChartCardHeaderAction();
  const keepsTable = !scrollable && hasFixedHeight && !!footer;

  return (
    <Card className="min-h-0 overflow-hidden">
      {/*
        Управление шапки встаёт в отведённое ему китом место (`CardAction` — вторая колонка
        шапки), а раскладку шапки задаёт потребитель: своего расположения кит ей не навязывает.
      */}
      <CardHeader className={cn('shrink-0', headerAction && 'grid')}>
        <CardTitle className="min-w-0">
          <TruncatedText>{title}</TruncatedText>
        </CardTitle>
        {headerAction && <CardAction>{headerAction}</CardAction>}
      </CardHeader>
      <CardContent className={cn('min-h-0 flex-1', !scrollable && 'flex flex-col')}>
        {scrollable ? (
          <ScrollArea className={cn('h-full', DASHBOARD_BLOCK_MAX_HEIGHT_CLASS)}>{children}</ScrollArea>
        ) : (
          <>
            {/*
              По признаку ячейка сетки растёт до содержимого карточки. Своими строками место таблицы
              её не растит — растила бы до всей таблицы, — в счёт идёт только наименьшая таблица.
            */}
            <div
              data-keeps-table={keepsTable || undefined}
              className={cn('flex-1 overflow-y-auto overflow-x-hidden', keepsTable ? 'contain-size' : 'min-h-0')}
              style={chartTableStyle(keepsTable)}
            >
              {children}
            </div>
            {footer}
          </>
        )}
      </CardContent>
    </Card>
  );
};
